import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { JobDescription } from "../../Entities/job-description.entity";
import JobDescriptionService from "../../Services/job-description.service";
import { useAppContext } from "../../Context/AppContext/AppContext";
import { useLoader } from "../LoaderContext/LoaderContext";
import { InterviewSession, jobPostRounds } from "../../Interface/Index";
import { useAuth } from "../AuthContext/AuthContext";
import { JobApplication } from "../../Entities/job-application.entity";
import { Userprofile } from "../../Entities/user-profile.entity";
import JobApplicationService from "../../Services/job.application.service";
import ProfileMatcherService from "../../Services/profile.matcher.service";
import { InterviewSchedule } from "../../Entities/interview-schedule.entity";
import InterviewService from "../../Services/interview.service";
import { useNavigate, useSearchParams } from "react-router-dom";

interface CurrentApplicationContextType {
  isInitDone: boolean;
  jobPost: JobDescription | null;
  rounds: jobPostRounds[];
  jobApplication: JobApplication | null;
  userProfile: Userprofile | null;
  interviewSessions: InterviewSession[];
  interviewSchedule: InterviewSchedule | null;
  currentView: string;

  // Methods
  refetch: () => Promise<void>;
  handleViewChange: (key: string) => void;

  // setters
  setJobApplication: React.Dispatch<
    React.SetStateAction<JobApplication | null>
  >;
  setUserProfile: React.Dispatch<React.SetStateAction<Userprofile | null>>;
  setInterviewSessions: React.Dispatch<
    React.SetStateAction<InterviewSession[]>
  >;
  setInterviewSchedule: React.Dispatch<
    React.SetStateAction<InterviewSchedule | null>
  >;
}

const CurrentApplicationContextContext = createContext<
  CurrentApplicationContextType | undefined
>(undefined);

interface ProviderProps {
  children: ReactNode;
  slug: string;
}

export const CurrentApplicationContextProvider = ({
  children,
  slug,
}: ProviderProps) => {
  const jobDescriptionService = JobDescriptionService.getInstance();
  const jobApplicationService = new JobApplicationService();
  const profileMatcherService = new ProfileMatcherService();
  const interviewService = new InterviewService();

  const navigate = useNavigate();

  const { organization } = useAppContext();
  const { setLoading, setLoadingMessage } = useLoader();
  const { user, isUserLoggedIn } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentView, setCurrentView] = useState(
    searchParams.get("view") || "apply",
  );

  const [isInitDone, setIsInitDone] = useState(false);

  const [jobPost, setJobPost] = useState<JobDescription | null>(null);
  const [rounds, setRounds] = useState<jobPostRounds[]>([]);

  const [jobApplication, setJobApplication] = useState<JobApplication | null>(
    null,
  );
  const [userProfile, setUserProfile] = useState<Userprofile | null>(null);

  const [interviewSessions, setInterviewSessions] = useState<
    InterviewSession[]
  >([]);
  const [interviewSchedule, setInterviewSchedule] =
    useState<InterviewSchedule | null>(null);

  const clearAllApplicationRelatedStates = () => {
    setJobApplication(null);
    setUserProfile(null);
    setInterviewSessions([]);
    setInterviewSchedule(null);
  };

  const loadApplicationDetails = async (
    jobPost: JobDescription,
    rounds: jobPostRounds[],
  ) => {
    try {
      // find job application for the user and job post
      const applicationResponse =
        await jobApplicationService.getJobApplicationByJobDescriptionId(
          jobPost._id,
        );

      if (!applicationResponse) {
        clearAllApplicationRelatedStates();
        return;
      }

      setLoadingMessage("Loading your application details...");
      setJobApplication(applicationResponse);

      // find userProfile if the resume is already uploaded
      if (
        applicationResponse.profileMatchStatus &&
        applicationResponse.profileId
      ) {
        const userProfileResponse =
          await profileMatcherService.getUserProfileById(
            applicationResponse.profileId,
          );
        if (userProfileResponse) {
          setUserProfile(userProfileResponse);
        }
      }

      // find interview sessions if rounds are defined for the job post
      if (rounds.length > 0) {
        const interviewSessionsResponse =
          await interviewService.getAllInterviewSessionById(
            applicationResponse._id,
          );
        setInterviewSessions(interviewSessionsResponse || []);
      } else {
        setInterviewSessions([]);
      }

      // find interview schedule if online interview is enabled for the job post
      if (
        jobPost &&
        jobPost.onlineInterview &&
        applicationResponse.interviewSchedule
      ) {
        const interviewScheduleResponse =
          await interviewService.getOnlineInterviewDetails(
            applicationResponse._id,
          );
        setInterviewSchedule(interviewScheduleResponse[0] || null);
      }
    } catch (error: any) {
      console.error("Error fetching application details:", error);
    }
  };

  const handleAuthenticatedUser = async (jobPostResponse: any) => {
    try {
      setJobPost(jobPostResponse.jobPost);
      setRounds(jobPostResponse.rounds);
      await loadApplicationDetails(
        jobPostResponse.jobPost,
        jobPostResponse.rounds,
      );
    } catch (error: any) {
      console.error("Error in handleAuthenticatedUser:", error);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
      setIsInitDone(true);
    }
  };

  const handleUnauthenticatedUser = async (jobPostResponse: any) => {
    setJobPost(jobPostResponse.jobPost);
    setRounds(jobPostResponse.rounds);
    clearAllApplicationRelatedStates();
    setLoading(false);
    setLoadingMessage(null);
    setIsInitDone(true);
  };

  const init = async (orgId: string) => {
    setLoading(true);
    setLoadingMessage("Loading job post details...");
    try {
      const response = await jobDescriptionService.findJobDescriptionBySlug(
        slug,
        orgId,
      );

      if (isUserLoggedIn && user) {
        handleAuthenticatedUser(response);
      } else {
        handleUnauthenticatedUser(response);
      }
    } catch (error) {
      console.error("Error fetching job description:", error);
      setJobPost(null);
      setRounds([]);
      clearAllApplicationRelatedStates();
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const refetch = async () => {
    if (jobPost) {
      setLoading(true);
      setLoadingMessage("Loading latest application details...");
      await loadApplicationDetails(jobPost, rounds);
      setLoading(false);
      setLoadingMessage(null);
      setIsInitDone(true);
    }
  };

  const handleViewChange = (key: string) => {
    const isRequirementPage = window.location.pathname.includes("requirements");
    if (!isRequirementPage) {
      navigate(`/${jobPost?.slug}/requirements?view=${key}`, { replace: true });
      return;
    }
    setSearchParams({ view: key }, { replace: true });
    setCurrentView(key);
  };

  useEffect(() => {
    setCurrentView(searchParams.get("view") || "apply");
  }, [searchParams]);

  useEffect(() => {
    const orgId = organization?._id;
    if (!orgId) {
      return;
    }

    init(orgId);
  }, [slug, organization, isUserLoggedIn, user]);

  return (
    <CurrentApplicationContextContext.Provider
      value={{
        isInitDone,
        jobPost,
        rounds,
        jobApplication,
        userProfile,
        interviewSessions,
        interviewSchedule,
        currentView,

        // Methods
        refetch,
        handleViewChange,

        // setters
        setJobApplication,
        setUserProfile,
        setInterviewSessions,
        setInterviewSchedule,
      }}
    >
      {children}
    </CurrentApplicationContextContext.Provider>
  );
};

export const useCurrentApplication = () => {
  const context = useContext(CurrentApplicationContextContext);
  if (!context) {
    throw new Error(
      "useCurrentApplicationContext must be used within CurrentApplicationContextProvider",
    );
  }
  return context;
};
